/**
 * Minimal client-side hash router.
 *
 * Routes:
 *   /                   → redirect to /dashboard or /login
 *   /login              → LoginPage
 *   /register           → RegisterPage
 *   /dashboard          → DashboardPage
 *   /editor/:projectId  → EditorPage
 *   /satellite/:projectId → SatelliteTracingPage
 *   /settings           → SettingsPage
 */

import { isAuthenticated } from '@/api';

// ============================================================
// Types
// ============================================================

export type RouteParams = Record<string, string>;

export interface Route {
  path: string;
  /** Regex pattern derived from path */
  pattern: RegExp;
  /** Named capture groups */
  paramNames: string[];
  /** Whether this route requires authentication */
  requiresAuth: boolean;
  /** Factory function: returns an HTMLElement to mount */
  render: (params: RouteParams) => HTMLElement;
}

export type RouteDefinition = Omit<Route, 'pattern' | 'paramNames'> & {
  path: string;
};

// ============================================================
// Path → Regex
// ============================================================

function pathToRegex(path: string): { pattern: RegExp; paramNames: string[] } {
  const paramNames: string[] = [];
  const regexStr = path
    .replace(/:[a-zA-Z]+/g, match => {
      paramNames.push(match.slice(1));
      return '([^/]+)';
    })
    .replace(/\//g, '\\/');
  return { pattern: new RegExp(`^${regexStr}$`), paramNames };
}

// ============================================================
// Router class
// ============================================================

class Router {
  private routes: Route[] = [];
  private outlet: HTMLElement | null = null;
  private notFoundFactory: (() => HTMLElement) | null = null;
  /** The last path successfully mounted — prevents double render from hashchange+popstate. */
  private lastMountedPath: string | null = null;

  init(outlet: HTMLElement): void {
    this.outlet = outlet;
    window.addEventListener('hashchange', () => this.handleNavigation());
    window.addEventListener('popstate', () => this.handleNavigation());

    // Initial render
    this.handleNavigation();
  }

  register(def: RouteDefinition): void {
    const { pattern, paramNames } = pathToRegex(def.path);
    this.routes.push({ ...def, pattern, paramNames });
  }

  onNotFound(factory: () => HTMLElement): void {
    this.notFoundFactory = factory;
  }

  navigate(path: string): void {
    window.location.hash = `#${path}`;
  }

  replace(path: string): void {
    window.location.replace(`${window.location.pathname}${window.location.search}#${path}`);
  }

  /** Returns the current path (hash without #, or '/'). */
  currentPath(): string {
    const hash = window.location.hash;
    return hash ? hash.slice(1) : '/';
  }

  private handleNavigation(): void {
    const path = this.currentPath();

    // Deduplicate: if both hashchange and popstate fire for the same path, skip
    if (path === this.lastMountedPath) return;

    for (const route of this.routes) {
      const match = path.match(route.pattern);
      if (!match) continue;

      // Guard: redirect to /login if auth required but not authenticated
      if (route.requiresAuth && !isAuthenticated()) {
        this.replace('/login');
        return;
      }

      // Guard: redirect to /dashboard if already authenticated on auth pages
      if (!route.requiresAuth && isAuthenticated() && (path === '/login' || path === '/register')) {
        this.replace('/dashboard');
        return;
      }

      const params: RouteParams = {};
      route.paramNames.forEach((name, i) => {
        params[name] = match[i + 1] ?? '';
      });

      this.mountPage(route.render(params), path);
      return;
    }

    // Root → redirect
    if (path === '/') {
      this.replace(isAuthenticated() ? '/dashboard' : '/login');
      return;
    }

    // 404
    if (this.notFoundFactory && this.outlet) {
      this.mountPage(this.notFoundFactory(), path);
    }
  }

  private mountPage(page: HTMLElement, path: string): void {
    if (!this.outlet) return;
    this.lastMountedPath = path;
    this.outlet.innerHTML = '';
    this.outlet.appendChild(page);

    // Scroll to top on navigation
    window.scrollTo(0, 0);
  }
}

export const router = new Router();
